import React from 'react';
import { useTranslation } from 'react-i18next';
import { BodyPortal } from '@/components/BodyPortal';
import { Button } from '@/components/Button';
import { CircleClock } from '@/components/Icons/CircleClock';
import { ClockFilled } from '@/components/Icons/ClockFilled';
import { WebcamVideo } from '@/components/Icons/WebcamVideo';
import { LessonDetailsChip } from '@/components/LessonDetailsModal/LessonDetailsChip';
import { useLessonVideoMeeting } from '@/components/LessonVideoMeeting/hooks/useLessonVideoMeeting';
import { Snackbar } from '@/components/Snackbar';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  BorderRadius,
  ButtonVariant,
  Size,
  SnackbarVariant,
} from '@/types/components';
import type { VideoMeetingResponse } from '@/types/video';

interface Props {
  meeting: VideoMeetingResponse;
}

export const LessonVideoCard: React.FC<Props> = ({ meeting: response }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    meeting,
    isJoinVisible,
    isErrorSnackbarOpen,
    join,
    closeErrorSnackbar,
  } = useLessonVideoMeeting(response);

  return (
    <div className="flex flex-col gap-5 rounded-xl bg-card p-4 shadow-card contrast:border contrast:border-border md:flex-row md:items-center md:gap-12">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 flex-col items-start gap-2 md:flex-row md:items-center">
          {!!meeting.title && (
            <p className="text-body-16 w-full min-w-0 truncate text-foreground md:w-auto">
              {meeting.title}
            </p>
          )}
          <LessonDetailsChip
            className="pl-1 whitespace-nowrap max-md:order-first"
            icon={
              <>
                <ClockFilled className="size-4 shrink-0 md:hidden" />
                <CircleClock className="size-4 shrink-0 max-md:hidden" />
              </>
            }
            label={meeting.time}
          />
        </div>
        <p className="text-body-13 md:text-body-14 min-w-0 truncate">
          <span className="text-muted-foreground max-md:font-medium">
            {meeting.authorName}
          </span>{' '}
          <span className="text-subtle-foreground">
            ({meeting.authorSubject})
          </span>
        </p>
      </div>

      {isJoinVisible && (
        <Button
          variant={ButtonVariant.Primary}
          size={isMobile ? Size.Large : Size.Small}
          radius={BorderRadius.Pill}
          className="shrink-0 max-md:w-full"
          onClick={join}
        >
          <WebcamVideo className="size-4 shrink-0 max-md:size-6" />
          {t('mail.video.join')}
        </Button>
      )}

      <BodyPortal>
        <Snackbar
          isOpen={isErrorSnackbarOpen}
          variant={SnackbarVariant.Error}
          message={t('global.errorOccurred')}
          onDismiss={closeErrorSnackbar}
        />
      </BodyPortal>
    </div>
  );
};
